import { ActiveOrderRow } from "@/lib/api/risk/brokerage/tt/v2";

/**
 * One leaf row in the Active Orders table = one customer split of one order.
 * Carries:
 *  - all the parent snapshot fields flattened at the top level (so the
 *    DataTable's normal accessors / sorters / filters work for both the leaf
 *    and the aggregate row), and
 *  - `parent`: the original ActiveOrderRow, untouched, so aggregate cells can
 *    read the parent verbatim instead of trying to roll up math from leaves.
 *  - `customer_id` / `customer_name` / `split_percentage` for the leaf.
 *
 * Each leaf inherits its parent's `guid_order_id`, which is also the grouping key.
 */
export interface ActiveOrderSplitRow {
  // ---- flattened parent fields (identical across all splits of an order) ----
  guid_order_id: string;
  exchange_event_time: string;
  trading_account_name: string | null;
  user_name: string | null;
  instrument_name: string | null;
  buy_sell_type: string | null;
  order_quantity: string | null;
  order_uom_quantity: string | null;
  working_quantity: string | null;
  remaining_quantity: string | null;
  filled_quantity: string | null;
  limit_price: string | null;
  text_a: string | null;
  text_b: string | null;
  text_c: string | null;
  text_tt: string | null;

  // ---- split-level fields ----
  customer_id: string | null;
  customer_name: string | null;
  split_percentage: number | string | null;

  // ---- pristine parent reference for aggregate-cell rendering ----
  parent: ActiveOrderRow;
}

/**
 * Explode one ActiveOrderRow into one row per SpecifiedCustomer split.
 * Orders without metadata or without splits are dropped (the backend already
 * filters these out, but we defensively skip here too).
 */
export function explodeOrderToSplits(order: ActiveOrderRow): ActiveOrderSplitRow[] {
  const splits = order.metadata?.SpecifiedCustomers ?? [];
  if (splits.length === 0) return [];
  return splits.map((split) => ({
    guid_order_id: order.guid_order_id,
    exchange_event_time: order.exchange_event_time,
    trading_account_name: order.trading_account_name,
    user_name: order.user_name,
    instrument_name: order.instrument_name,
    buy_sell_type: order.buy_sell_type,
    order_quantity: order.order_quantity,
    order_uom_quantity: order.order_uom_quantity,
    working_quantity: order.working_quantity,
    remaining_quantity: order.remaining_quantity,
    filled_quantity: order.filled_quantity,
    limit_price: order.limit_price,
    text_a: order.text_a,
    text_b: order.text_b,
    text_c: order.text_c,
    text_tt: order.text_tt,

    customer_id: split.customer_id ?? null,
    customer_name: split.Customer?.long_name ?? null,
    split_percentage: split.split_percentage ?? null,

    parent: order,
  }));
}
